import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MapPin, Clock, Users, ArrowLeft, Share2, Calendar as CalIcon, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/events/${id}`).then(r => setEvent(r.data)).catch(() => toast.error('Evenement introuvable')).finally(() => setLoading(false));
  }, [id]);

  const handleReserve = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      await api.post('/reservations', { event_id: id, type: 'event', guests: 1 });
      toast.success('Reservation envoyee !');
      navigate('/reservations');
    } catch (err) {
      toast.error('Erreur lors de la reservation');
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Lien copie !');
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;
  if (!event) return <div className="text-center py-20 text-[#A1A1AA]">Evenement introuvable</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 fade-in" data-testid="event-detail-page">
      <Link to="/events" className="inline-flex items-center gap-1 text-sm text-[#A1A1AA] hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour aux evenements
      </Link>

      <div className="bg-[#121212] border border-white/5 rounded-2xl overflow-hidden">
        <div className="h-56 relative overflow-hidden">
          {event.image_url ? (
            <>
              <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/15 to-transparent flex items-center justify-center">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37]" style={{ fontFamily: 'Outfit' }}>{new Date(event.date).getDate()}</div>
                <div className="text-lg text-[#A1A1AA]">{new Date(event.date).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit' }}>{event.title}</h1>

          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
              <div className="bg-[#D4AF37]/10 p-2 rounded-lg"><MapPin className="w-4 h-4 text-[#D4AF37]" /></div>
              <div><div className="text-white">{event.location}</div><div className="text-xs">{event.address}</div></div>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
              <div className="bg-[#D4AF37]/10 p-2 rounded-lg"><Clock className="w-4 h-4 text-[#D4AF37]" /></div>
              <div>
                <div className="text-white">{new Date(event.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                {event.end_date && <div className="text-xs">Fin: {new Date(event.end_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>}
              </div>
            </div>
            {event.capacity && (
              <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
                <div className="bg-[#D4AF37]/10 p-2 rounded-lg"><Users className="w-4 h-4 text-[#D4AF37]" /></div>
                <div className="text-white">{event.capacity} places</div>
              </div>
            )}
          </div>

          <p className="text-[#A1A1AA] leading-relaxed mb-8">{event.description}</p>

          {event.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {event.tags.map(t => (
                <span key={t} className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full">{t}</span>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-white/5">
            <div className="flex-1">
              {event.price > 0 ? (
                <div className="text-2xl font-bold text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{event.price.toFixed(2)} EUR</div>
              ) : (
                <div className="text-xl font-semibold text-[#10B981]">Entree gratuite</div>
              )}
            </div>
            <div className="flex gap-2">
              {event.google_cal_link && (
                <a href={event.google_cal_link} target="_blank" rel="noreferrer" data-testid="event-gcal-btn">
                  <Button variant="outline" className="border-white/10 hover:border-[#D4AF37] rounded-full px-4">
                    <CalIcon className="w-4 h-4" />
                  </Button>
                </a>
              )}
              <Button onClick={handleShare} variant="outline" data-testid="event-share-btn"
                className="border-white/10 hover:border-[#D4AF37] rounded-full px-4">
                <Share2 className="w-4 h-4" />
              </Button>
              {event.ticket_url ? (
                <a href={event.ticket_url} target="_blank" rel="noreferrer" data-testid="event-ticket-btn">
                  <Button className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-8 font-semibold">
                    <Ticket className="w-4 h-4 mr-2" /> Prendre mon billet
                  </Button>
                </a>
              ) : (
                <Button onClick={handleReserve} data-testid="event-reserve-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-8 font-semibold">
                  <CalIcon className="w-4 h-4 mr-2" /> {"Réserver"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
